import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { FC } from "react";
import { useSelector } from "react-redux";

import { OpenInNew } from "@livechat/design-system-icons";
import {
  Alert,
  Button,
  DesignToken,
  Heading,
  Icon,
  Link,
  ShadowToken,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { config } from "config/config";
import { HelpCenter } from "constants/help-center";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { PageWrapper, SettingsContentWrapper } from "routes/settings/styled";
import { selectHasLiveChatIntegration } from "store/entities/integrations";
import { withVar } from "themes/helpers";

import { LiveChatDataImport } from "./LiveChatDataImport";

export const LivechatSettings: FC = () => {
  const isLiveChatIntegrationEnabled = useSelector(selectHasLiveChatIntegration);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleExploreLCMarketplaceClick = () => {
    trackEngagement("LC integration: Explore LC Marketplace button clicked", EventPlace.Settings);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleLearnMoreClick = () => {
    trackEngagement("LC integration: Learn more clicked", EventPlace.Settings);
  };

  return (
    <PageWrapper>
      <SettingsContentWrapper>
        {isLiveChatIntegrationEnabled ? (
          <Text>
            Your integration with LiveChat is up and running.{" "}
            <Link
              target="_blank"
              rel="nofollow noreferrer noopener"
              href={config.livechatMarketplaceAppLink}
              onClick={handleExploreLCMarketplaceClick}
              bold={false}
            >
              Open the Livechat Marketplace
            </Link>{" "}
            to manage it.
          </Text>
        ) : (
          <>
            <Text
              css={css`
                margin-bottom: ${withVar(SpacingToken.Spacing3)};
              `}
            >
              Integrate HelpDesk and LiveChat to create a powerful customer support platform.
            </Text>
            <Button
              href={config.livechatMarketplaceAppLink}
              target="_blank"
              rel="nofollow noreferrer noopener"
              size="compact"
              kind="secondary"
              iconPosition="right"
              icon={<Icon source={OpenInNew} />}
              onClick={handleExploreLCMarketplaceClick}
            >
              Explore LiveChat Marketplace
            </Button>
          </>
        )}

        <Block marginTop="30px">
          <Heading
            size="xs"
            css={css`
              padding: var(${SpacingToken.Spacing1}) 0;
              box-shadow: var(${ShadowToken.DividerBottom});
              margin-bottom: var(${SpacingToken.Spacing4});
            `}
          >
            Import LiveChat data
          </Heading>
          <Text>
            Import agents, groups, tags, and canned responses from LiveChat to HelpDesk.{" "}
            <Link
              target="_blank"
              rel="nofollow noreferrer noopener"
              href={HelpCenter.LIVECHAT_INTEGRATION_IMPORT_DATA_FROM_LIVECHAT_TO_HELPDESK}
              onClick={handleLearnMoreClick}
              bold={false}
            >
              Learn more
            </Link>
          </Text>

          <Block marginTop={withVar(SpacingToken.Spacing4)}>
            {isLiveChatIntegrationEnabled ? (
              <LiveChatDataImport />
            ) : (
              <StyledAlert kind="info">
                <Text
                  size="md"
                  css={css`
                    margin-bottom: ${withVar(SpacingToken.Spacing3)};
                  `}
                >
                  Integrate HelpDesk with LiveChat to import your data.
                </Text>
                <StyledButtonLink
                  href={config.livechatMarketplaceAppLink}
                  target="_blank"
                  kind="link"
                  rel="nofollow noreferrer noopener"
                  onClick={handleExploreLCMarketplaceClick}
                >
                  <Text underline bold>
                    Integrate HelpDesk with LiveChat
                  </Text>
                </StyledButtonLink>
              </StyledAlert>
            )}
          </Block>
        </Block>
      </SettingsContentWrapper>
    </PageWrapper>
  );
};

const StyledAlert = styled(Alert)`
  max-width: 724px;
`;

const StyledButtonLink = styled(Button)`
  color: ${withVar(DesignToken.ActionHighContrastDefault)};

  &:hover {
    color: ${withVar(DesignToken.ActionHighContrastHover)};
  }
`;
